import { useEffect, useMemo, useState } from "react";
import { api, API, downloadCsv } from "../lib/api";
import { toast } from "sonner";
import { Download, Search, TrendingUp, Users, Eye, ShoppingBag, Repeat, BookOpen, RefreshCw, BarChart3 } from "lucide-react";
import { ClientLink } from "./AdminClient";
import { BarChart, Bar as RBar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";

const CHART_COLORS = ["#A3876A", "#7B8A72", "#B87333", "#8C7B9E", "#C9A18A", "#5C6B73", "#D4B483", "#A86962", "#6E8B74"];

function MonthlyRevenue() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/stats/revenue-monthly?months=12").then(r => setD(r.data)); }, []);
  if (!d) return null;
  const products = d.products || [];
  const total12 = d.months.reduce((a, m) => a + (m.total || 0), 0);
  const best = d.months.reduce((a, m) => (m.total > (a?.total || 0) ? m : a), null);
  return (
    <div data-testid="monthly-revenue" className="mt-6 bg-[#FDFBF7] border border-[#E8DCC8] p-5">
      <div className="flex flex-wrap items-baseline justify-between gap-3 mb-3">
        <p className="font-serif text-lg text-[#2C2621] flex items-center gap-2"><BarChart3 size={16} className="text-[#A3876A]"/> Chiffre d'affaires mensuel — 12 derniers mois</p>
        <p className="text-xs text-[#7C7064]">Total : <strong className="text-[#2C2621]">{total12.toLocaleString("fr-FR")} €</strong>{best?.total > 0 && <> · meilleur mois : {best.label} ({best.total.toLocaleString("fr-FR")} €)</>}</p>
      </div>
      <div style={{ width: "100%", height: 280 }}>
        <ResponsiveContainer>
          <BarChart data={d.months} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#7C7064" }} axisLine={false} tickLine={false}/>
            <YAxis tick={{ fontSize: 11, fill: "#7C7064" }} axisLine={false} tickLine={false} tickFormatter={v => `${v} €`}/>
            <Tooltip formatter={(v, n) => [`${Number(v).toLocaleString("fr-FR")} €`, n]} contentStyle={{ border: "1px solid #E8DCC8", background: "#FDFBF7", fontSize: 12 }}/>
            <Legend wrapperStyle={{ fontSize: 11 }}/>
            {products.map((p, i) => <RBar key={p.key} dataKey={p.key} name={p.label} stackId="a" fill={CHART_COLORS[i % CHART_COLORS.length]}/>)}
          </BarChart>
        </ResponsiveContainer>
      </div>
      <table className="w-full text-xs mt-4">
        <thead><tr className="text-left text-[#7C7064] uppercase tracking-wider text-[10px]">
          <th className="py-1">Mois</th>{products.map(p => <th key={p.key} className="py-1 text-right">{p.label}</th>)}<th className="py-1 text-right">Total</th>
        </tr></thead>
        <tbody>{d.months.slice().reverse().map(m => (
          <tr key={m.month} className="border-t border-[#E8DCC8]">
            <td className="py-1.5">{m.label}</td>
            {products.map(p => <td key={p.key} className="py-1.5 text-right text-[#4A4036]">{m[p.key] ? `${m[p.key].toLocaleString("fr-FR")} €` : "—"}</td>)}
            <td className="py-1.5 text-right font-serif text-sm">{m.total.toLocaleString("fr-FR")} €</td>
          </tr>))}
        </tbody>
      </table>
    </div>
  );
}

function BackfillButton({ onDone }) {
  const [busy, setBusy] = useState(false);
  async function run() {
    if (!window.confirm("Recalculer les montants réels de toutes les commandes payées ? Cette opération est idempotente et sûre.")) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/backfill-order-amounts");
      toast.success(`Recalcul terminé : ${r.data.updated} commande(s) mises à jour sur ${r.data.scanned} analysées.`);
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors du recalcul");
    } finally { setBusy(false); }
  }
  return (
    <button data-testid="backfill-amounts-btn" onClick={run} disabled={busy}
      className="btn-outline text-xs flex items-center gap-2">
      <RefreshCw size={13} className={busy ? "animate-spin" : ""}/>
      {busy ? "Recalcul en cours…" : "Recalculer avec les montants réels"}
    </button>
  );
}

// Palette éditoriale — badges par source
const SOURCE_COLORS = {
  "Cadeau bienvenue": "#B5A88C",
  "Newsletter": "#A3876A",
  "Programme Essentiel": "#7B8A72",
  "Programme Épistolaire": "#5F6E56",
  "Correspondance (1 lettre)": "#C7A46B",
  "Correspondance (coffret 3 lettres)": "#A88E5A",
  "Abonnement mensuel": "#8A7B9E",
  "Bon cadeau — Essentiel": "#D0A88A",
  "Bon cadeau — Épistolaire": "#B58C6E",
  "Bon cadeau — 1 lettre": "#D0A88A",
  "Bon cadeau — coffret": "#B58C6E",
  "Bon cadeau": "#D0A88A",
  "Formulaire contact": "#9E9082",
  "Import manuel": "#7C7064",
};

function Badge({ label }) {
  const bg = SOURCE_COLORS[label] || "#A3876A";
  return (
    <span
      className="inline-block px-2 py-0.5 text-[11px] text-white mr-1 mb-1"
      style={{ backgroundColor: bg }}
    >
      {label}
    </span>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("fr-FR"); } catch { return "—"; }
}

// ===================== CONTACTS =====================
export function AdminContacts() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/contacts")
      .then(r => setItems(r.data.items || []))
      .finally(() => setLoading(false));
  }, []);

  const allSources = useMemo(() => {
    const s = new Set();
    items.forEach(c => (c.sources || []).forEach(x => s.add(x)));
    return Array.from(s).sort();
  }, [items]);

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return items.filter(c => {
      if (ql && !c.email.includes(ql) && !(c.name || "").toLowerCase().includes(ql)) return false;
      if (filter && !(c.sources || []).includes(filter)) return false;
      return true;
    });
  }, [items, q, filter]);

  function downloadCsv() {
    const token = localStorage.getItem("rdv_token");
    fetch(`${API}/admin/contacts.csv`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(b => {
        const url = URL.createObjectURL(b);
        const a = document.createElement("a");
        a.href = url; a.download = "contacts.csv"; a.click();
        URL.revokeObjectURL(url);
      });
  }

  return (
    <div data-testid="admin-contacts">
      <div className="flex items-center justify-between mb-8">
        <div>
          <p className="font-hand text-xl text-[#A3876A]">Carnet d'adresses</p>
          <h1 className="font-serif text-4xl">Contacts</h1>
        </div>
        <div className="flex items-center gap-4">
          <p className="font-serif text-3xl" data-testid="contacts-total">{items.length}</p>
          <button data-testid="contacts-export" onClick={downloadCsv}
            className="flex items-center gap-2 px-4 py-2 border border-[#A3876A] text-[#A3876A] hover:bg-[#A3876A] hover:text-white transition-colors text-sm">
            <Download size={14}/> Exporter CSV
          </button>
        </div>
      </div>

      <div className="bg-[#F5F0E6] border border-[#E8DCC8] p-4 mb-4 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7C7064]"/>
          <input
            data-testid="contacts-search"
            className="w-full pl-9 pr-3 py-2 bg-white border border-[#E8DCC8] text-sm"
            placeholder="Rechercher un email ou un prénom…"
            value={q} onChange={e => setQ(e.target.value)}
          />
        </div>
        <select
          data-testid="contacts-filter"
          className="px-3 py-2 bg-white border border-[#E8DCC8] text-sm"
          value={filter} onChange={e => setFilter(e.target.value)}
        >
          <option value="">Toutes les sources</option>
          {allSources.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <span className="text-xs text-[#7C7064]">
          {filtered.length} sur {items.length}
        </span>
      </div>

      <div className="bg-[#FDFBF7] border border-[#E8DCC8] overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#F5F0E6] text-left">
            <tr>
              <th className="p-3">Email</th>
              <th className="p-3">Nom</th>
              <th className="p-3">Sources d'acquisition</th>
              <th className="p-3">Premier contact</th>
              <th className="p-3">Dernier contact</th>
              <th className="p-3">Newsletter</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan="6" className="p-8 text-center text-[#7C7064]">Chargement…</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan="6" className="p-8 text-center text-[#7C7064]">Aucun contact.</td></tr>
            )}
            {filtered.map(c => (
              <tr key={c.email} className="border-t border-[#E8DCC8] hover:bg-[#FBF6EC]">
                <td className="p-3 font-mono text-xs"><ClientLink email={c.email}/></td>
                <td className="p-3">{c.name || "—"}</td>
                <td className="p-3">
                  {(c.sources || []).map(s => <Badge key={s} label={s}/>)}
                </td>
                <td className="p-3 text-xs text-[#7C7064]">{fmtDate(c.first_seen)}</td>
                <td className="p-3 text-xs text-[#7C7064]">{fmtDate(c.last_seen)}</td>
                <td className="p-3 text-xs">
                  {c.newsletter_consent ? <span className="text-[#7B8A72]">✓ oui</span> : <span className="text-[#7C7064]">—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ===================== STATISTIQUES =====================
function Kpi({ label, value, sub, Icon, color = "#A3876A" }) {
  return (
    <div className="bg-[#FDFBF7] border border-[#E8DCC8] p-5">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs uppercase tracking-wider text-[#7C7064]">{label}</p>
        {Icon && <Icon size={16} style={{ color }}/>}
      </div>
      <p className="font-serif text-3xl text-[#2C2621]">{value}</p>
      {sub && <p className="text-xs text-[#7C7064] mt-1">{sub}</p>}
    </div>
  );
}

function Bar({ label, count, max, color = "#A3876A" }) {
  const pct = max ? Math.max(4, Math.round((count / max) * 100)) : 0;
  return (
    <div className="mb-2">
      <div className="flex justify-between text-xs text-[#4A4036] mb-1">
        <span className="truncate pr-3">{label}</span>
        <span className="tabular-nums">{count}</span>
      </div>
      <div className="h-2 bg-[#F5F0E6]">
        <div className="h-full" style={{ width: `${pct}%`, backgroundColor: color }}/>
      </div>
    </div>
  );
}

const PAGE_NAMES = {
  "/": "Accueil", "/club-de-lettres": "Club de Lettres", "/club-de-lettres/offrir": "Offrir le Club", "/club-de-lettres/resilier": "Résiliation Club",
  "/programme": "Programme 52 semaines", "/programmes": "Les programmes", "/correspondance": "Correspondance", "/ateliers": "Ateliers",
  "/journal": "Journal", "/a-propos": "À propos", "/carte-cadeau": "Offrir (carte cadeau)", "/contact": "Contact", "/faq": "FAQ",
  "/mail-club": "Club de Lettres (ancienne adresse)", "/mail-club/offrir": "Offrir le Club (ancienne adresse)", "/mail-club/inscription": "Club — inscription (ancienne adresse)",
  "/espace": "Mon espace", "/espace/verify": "Mon espace (connexion)", "/cgv": "CGV", "/accompagnement": "Accompagnement",
};
const fmtDur = (secs) => {
  if (secs == null) return "—";
  if (secs < 60) return `${secs} s`;
  const m = Math.floor(secs / 60), r = secs % 60;
  return r ? `${m} min ${r.toString().padStart(2, "0")}` : `${m} min`;
};

export function AdminStats() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [month, setMonth] = useState(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; });

  useEffect(() => {
    let alive = true;
    const load = () => api.get("/admin/stats").then(r => { if (alive) { setData(r.data); setUpdatedAt(new Date()); } }).finally(() => alive && setLoading(false));
    load();
    const id = setInterval(() => { if (document.visibilityState === "visible") load(); }, 30000);
    const onVis = () => { if (document.visibilityState === "visible") load(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { alive = false; clearInterval(id); document.removeEventListener("visibilitychange", onVis); };
  }, []);

  const exportAccounting = (scope) => downloadCsv(`/admin/export/accounting.csv?${scope}`, `ventes-${scope.split("=")[1]}.csv`)
    .catch(e => toast.error(`Export impossible (${e.message})`));

  if (loading || !data) {
    return <div data-testid="admin-stats" className="p-8 text-[#7C7064]">Chargement des statistiques…</div>;
  }

  const s = data.sales || {};
  const c = data.contacts || {};
  const p = data.programme || {};
  const t = data.traffic || {};

  const maxProduct = Math.max(1, ...(s.by_product || []).map(x => x.revenue_cents || 0));
  const maxSource = Math.max(1, ...(c.by_source || []).map(x => x.count || 0));
  const maxPage = Math.max(1, ...(t.top_referrers || []).map(x => x.count || 0));
  const maxDaily = Math.max(1, ...(t.daily || []).map(x => x.visitors || 0));

  return (
    <div data-testid="admin-stats" className="space-y-10">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="font-hand text-xl text-[#A3876A]">Tableau de bord</p>
          <h1 className="font-serif text-4xl">Statistiques</h1>
          {updatedAt && <p data-testid="stats-live" className="text-[11px] text-[#7C7064] mt-1 flex items-center gap-1.5"><span className="inline-block w-1.5 h-1.5 rounded-full bg-[#7B8A72] animate-pulse"/> En direct · actualisé à {updatedAt.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</p>}
        </div>
        <div data-testid="accounting-export" className="flex flex-wrap items-center gap-2 text-sm">
          <input type="month" value={month} onChange={e => setMonth(e.target.value)} className="px-2 py-1.5 bg-white border border-[#E8DCC8] text-sm"/>
          <button data-testid="export-accounting-month" onClick={() => exportAccounting(`month=${month}`)} className="btn-outline inline-flex items-center gap-1.5 !py-1.5"><Download size={13}/> Export comptable du mois</button>
          <button data-testid="export-accounting-year" onClick={() => exportAccounting(`year=${month.slice(0, 4)}`)} className="text-xs text-[#A3876A] hover:underline">Toute l'année {month.slice(0, 4)}</button>
          <button data-testid="cleanup-tests" onClick={async () => {
            try {
              const pre = await api.get("/admin/cleanup-tests");
              if (!pre.data.total_emails) return toast.info("Aucune donnée de test trouvée");
              if (!window.confirm(`Supprimer les données de test de ${pre.data.total_emails} adresse(s) ?\n${Object.entries(pre.data.found).map(([k, v]) => `${k} : ${v.length}`).join("\n")}`)) return;
              const r = await api.post("/admin/cleanup-tests");
              toast.success(`Nettoyé : ${Object.values(r.data.deleted).reduce((a, b) => a + b, 0)} fiche(s) supprimée(s)`);
              api.get("/admin/stats").then(x => setData(x.data));
            } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
          }} className="text-xs text-[#A86962] hover:underline">Nettoyer les données de test</button>
        </div>
      </div>

      {/* Ventes */}
      {/* Trafic — en tête, centré sur les personnes */}
      <section data-testid="traffic-section">
        <h2 className="font-serif text-2xl text-[#2C2621] mb-1 flex items-center gap-2">
          <Eye size={18} className="text-[#A3876A]"/> Visiteurs du site, jour par jour
        </h2>
        <p className="text-xs text-[#7C7064] mb-4">Un visiteur = une personne (même appareil) sur la journée, quel que soit le nombre de pages ouvertes.</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Kpi label="Visiteurs aujourd'hui" value={t.visitors_today || 0}
               sub={`hier : ${t.visitors_yesterday || 0}`} Icon={Eye}/>
          <Kpi label="Moyenne par jour (7 j)" value={t.avg_visitors_7d ?? 0}
               sub={`${t.unique_7d || 0} personne${(t.unique_7d||0)>1?"s":""} différente${(t.unique_7d||0)>1?"s":""} sur 7 jours`}/>
          <Kpi label="Temps moyen par visite" value={fmtDur(t.session?.avg_seconds)}
               sub={t.session?.avg_pages ? `${t.session.avg_pages} page${t.session.avg_pages>1?"s":""} vue${t.session.avg_pages>1?"s":""} en moyenne` : "mesure en cours…"}/>
          <Kpi label="Visiteurs au total" value={t.visitors_total || 0}
               sub={`depuis le début · ${t.unique_30d || 0} sur 30 j`}/>
        </div>

        {(t.daily || []).length > 0 && (
          <div data-testid="traffic-daily-chart" className="bg-[#FDFBF7] border border-[#E8DCC8] p-5 mt-4">
            <div className="flex items-end gap-[3px] h-40">
              {(t.daily || []).map((d, i) => {
                const h = Math.max(d.visitors ? 6 : 2, Math.round((d.visitors / maxDaily) * 100));
                const isToday = i === t.daily.length - 1;
                return (
                  <div key={d.day} className="flex-1 h-full flex flex-col items-center justify-end group relative" title={`${new Date(d.day).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })} — ${d.visitors} visiteur${d.visitors>1?"s":""}`}>
                    <span className="absolute -top-5 text-[10px] text-[#2C2621] opacity-0 group-hover:opacity-100 transition-opacity">{d.visitors}</span>
                    <div className={`w-full ${isToday ? "bg-[#C35A3E]" : "bg-[#A3876A] group-hover:bg-[#8A7158]"}`} style={{ height: `${h}%` }}/>
                  </div>
                );
              })}
            </div>
            <div className="flex justify-between text-[10px] text-[#7C7064] mt-2">
              <span>{new Date(t.daily[0].day).toLocaleDateString("fr-FR", { day: "numeric", month: "short" })}</span>
              <span>il y a 15 jours</span>
              <span className="text-[#C35A3E]">aujourd'hui</span>
            </div>
          </div>
        )}

        <div className="grid md:grid-cols-3 gap-4 mt-4">
          <div className="md:col-span-2 bg-[#FDFBF7] border border-[#E8DCC8] p-5" data-testid="traffic-pages">
            <p className="font-serif text-lg mb-1 text-[#2C2621]">Quelles pages visitent-ils ? (30 j)</p>
            <p className="text-xs text-[#7C7064] mb-4">Part des visiteurs ayant ouvert la page, et temps moyen passé dessus.</p>
            {(t.top_pages || []).length === 0 && (
              <p className="text-sm text-[#7C7064]">Aucune visite enregistrée pour l'instant. Le suivi démarre maintenant ✦</p>
            )}
            <ul className="space-y-3">
              {(t.top_pages || []).map(row => (
                <li key={row.path} data-testid={`traffic-page-${row.path}`}>
                  <div className="flex items-baseline justify-between gap-3 text-sm">
                    <span className="truncate text-[#2C2621]">{PAGE_NAMES[row.path] || row.path}</span>
                    <span className="shrink-0 text-[#7C7064] text-xs">
                      <strong className="text-[#2C2621] text-sm">{row.visitors}</strong> visiteur{row.visitors>1?"s":""} · {row.share} % · <span title="Temps moyen passé sur la page">{fmtDur(row.avg_seconds)}</span>
                    </span>
                  </div>
                  <div className="h-1.5 bg-[#F0E8DA] mt-1"><div className="h-1.5 bg-[#7B8A72]" style={{ width: `${row.share}%` }}/></div>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-[#FDFBF7] border border-[#E8DCC8] p-5">
            <p className="font-serif text-lg mb-3 text-[#2C2621]">D'où viennent-ils ? (30 j)</p>
            {(t.top_referrers || []).length === 0 && (
              <p className="text-sm text-[#7C7064]">Aucune source externe détectée pour l'instant (accès direct, Instagram in-app…).</p>
            )}
            {(t.top_referrers || []).map(row => (
              <Bar key={row.source} label={row.source} count={row.count} max={maxPage} color="#8A7B9E"/>
            ))}
          </div>
        </div>
      </section>

      <section>
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <h2 className="font-serif text-2xl text-[#2C2621] flex items-center gap-2">
            <ShoppingBag size={18} className="text-[#A3876A]"/> Ventes
          </h2>
          <BackfillButton onDone={() => api.get("/admin/stats").then(r => setData(r.data))}/>
        </div>
        <p className="text-xs text-[#7C7064] mb-4 italic">
          Le CA reflète uniquement les montants réellement encaissés (commandes + prélèvements Club de Lettres + Club de Lettres offerts), à la date du paiement. Les cartes cadeau utilisées et les codes promo à 100&nbsp;% ne sont pas comptabilisés.
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Kpi label="CA total" value={`${s.revenue_total?.toLocaleString("fr-FR") || 0} €`}
               sub={`${s.total_orders} commande${s.total_orders>1?"s":""}`} Icon={TrendingUp}/>
          <Kpi label="CA 30 j" value={`${s.revenue_30d?.toLocaleString("fr-FR") || 0} €`}
               sub={`${s.orders_30d} commande${s.orders_30d>1?"s":""}`}/>
          <Kpi label="CA 7 j" value={`${s.revenue_7d?.toLocaleString("fr-FR") || 0} €`}
               sub={`${s.orders_7d} commande${s.orders_7d>1?"s":""}`}/>
          <Kpi label="Abonnements actifs" value={s.subs_active || 0}
               sub={`Club de Lettres : ${s.subs_mailclub_active || 0} payants · ${s.subs_gift_active || 0} offerts · MRR ${s.subs_mrr?.toLocaleString("fr-FR") || 0} €`} Icon={Repeat}/>
        </div>
        <MonthlyRevenue/>

        <div className="mt-6 bg-[#FDFBF7] border border-[#E8DCC8] p-5">
          <p className="font-serif text-lg mb-3 text-[#2C2621]">Répartition par produit</p>
          {(s.by_product || []).length === 0 && (
            <p className="text-sm text-[#7C7064]">Aucune vente enregistrée pour le moment.</p>
          )}
          {(s.by_product || []).map(pr => (
            <Bar key={pr.key}
                 label={`${pr.label} — ${pr.count} vente${pr.count>1?"s":""}`}
                 count={`${pr.revenue.toLocaleString("fr-FR")} €`}
                 max={maxProduct}
                 color={SOURCE_COLORS[pr.label] || "#A3876A"}/>
          ))}
        </div>
      </section>

      {/* Contacts */}
      <section>
        <h2 className="font-serif text-2xl text-[#2C2621] mb-4 flex items-center gap-2">
          <Users size={18} className="text-[#A3876A]"/> Contacts
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <Kpi label="Total" value={c.total || 0}/>
          <Kpi label="Nouveaux (30 j)" value={c.new_30d || 0}/>
          <Kpi label="Nouveaux (7 j)" value={c.new_7d || 0}/>
        </div>
        <div className="mt-6 bg-[#FDFBF7] border border-[#E8DCC8] p-5">
          <p className="font-serif text-lg mb-3 text-[#2C2621]">Répartition par source</p>
          {(c.by_source || []).length === 0 && (
            <p className="text-sm text-[#7C7064]">Aucun contact.</p>
          )}
          {(c.by_source || []).map(row => (
            <Bar key={row.label} label={row.label} count={row.count} max={maxSource}
                 color={SOURCE_COLORS[row.label] || "#A3876A"}/>
          ))}
        </div>
      </section>

      {/* Programme */}
      <section>
        <h2 className="font-serif text-2xl text-[#2C2621] mb-4 flex items-center gap-2">
          <BookOpen size={18} className="text-[#A3876A]"/> Programme 52 semaines
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Kpi label="Participantes actives" value={p.active || 0}/>
          <Kpi label="Total inscrites" value={p.total || 0}/>
          <Kpi label="Semaine moyenne" value={p.avg_week || 0}
               sub={`sur 52`}/>
          <Kpi label="Ont terminé" value={p.completed || 0}/>
        </div>
      </section>

    </div>
  );
}
